import {
  HttpEvent,
  HttpInterceptorFn,
  HttpRequest,
  HttpErrorResponse,
  HttpHandlerFn,
} from '@angular/common/http';
import { finalize, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { CONFIG_SERVICE } from '../tokens/interceptor.tokens';
import { inject } from '@angular/core';
import { DebugLogType } from '../models/interceptor.models';

/**
 * Debugging Interceptor
 *
 * This interceptor logs detailed information about HTTP requests and responses, such as headers,
 * body, status codes, and performance data. The logging behavior is dynamically configurable
 * through the consuming application's configuration service.
 *
 * Configuration Options:
 * - `debugMode`: Enables or disables all debugging logs.
 * - `logTypes`: Specifies which log types to enable (e.g., request, response, error, performance, finalize).
 * - `threshold`: Specifies the performance threshold in milliseconds for logging warnings.
 */
export const debugInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  /**
   * Inject the configuration service to retrieve the debug configuration.
   * If the service is unavailable or misconfigured, an error will be logged.
   */
  const configService = inject(CONFIG_SERVICE, { optional: true });
  const startTime = Date.now(); // Track the start time for performance measurement.

  if (!configService?.getDebugConfig()) {
    console.error('Debug Interceptor Error: Configuration not found.');
    return next(req);
  }

  // Retrieve configuration from the service.
  const config = configService.getDebugConfig();
  const debugMode = config.debugMode || false; // Global flag to enable/disable debugging.
  const logTypes = config.logTypes || {}; // Object containing flags for individual log types.
  const threshold = config.threshold || 300; // Performance threshold for warnings in milliseconds.

  /**
   * Utility function to conditionally log messages based on the `logTypes` configuration.
   *
   * @param type - The log type to check (e.g., 'request', 'response').
   * @param logFn - The logging function to execute if the log type is enabled.
   */
  const logIfEnabled = (type: DebugLogType, logFn: () => void) => {
    if (debugMode && logTypes[type]) {
      logFn();
    }
  };

  // Log the request details if the 'request' log type is enabled.
  logIfEnabled('request', () => {
    console.group(`HTTP Request: ${req.method} ${req.url}`);
    console.log('Headers:', req.headers);
    console.log('Body:', req.body);
    console.groupEnd();
  });

  // Pass the request to the next handler and process the response.
  return next(req).pipe(
    tap({
      /**
       * Handles successful HTTP responses.
       *
       * Logs response details such as status, headers, body, and performance data.
       * Logs a warning if the request duration exceeds the configured threshold.
       *
       * @param event - The HTTP event, expected to be a response.
       */
      next: (event: HttpEvent<unknown>) => {
        if (event.type === 4 /* HttpEventType.Response */) {
          const endTime = Date.now();
          const duration = endTime - startTime; // Calculate request duration.

          logIfEnabled('response', () => {
            console.groupCollapsed(`HTTP Response: ${req.method} ${req.url}`);
            console.log('Status:', event['status']);
            console.log('Duration:', `${duration}ms`)
            console.log('Headers:', event['headers']);
            console.log('Body:', event['body']);
            console.groupEnd();
          });

          logIfEnabled('performance', () => {
            if (duration > threshold) {
              console.warn(
                `Warning: Request to [${req.method}] ${req.url} exceeded ${threshold}ms, took ${duration}ms.`
              );
            }
          });
        }
      },

      /**
       * Handles HTTP errors.
       *
       * Logs error details such as status, message, error body, and performance data.
       *
       * @param error - The HTTP error response.
       */
      error: (error: HttpErrorResponse) => {
        const endTime = Date.now();
        const duration = endTime - startTime; // Calculate request duration.

        logIfEnabled('error', () => {
          console.groupCollapsed(`HTTP Error: ${req.method} ${req.url}`);
          console.error('Status:', error.status);
          console.error('Message:', error.message);
          console.error('Error Body:', error.error);
          logDebug(debugMode, `Duration ${duration}ms`);
          console.groupEnd();
        });
      },
    }),

    /**
     * Executes cleanup logic once the request is finalized.
     *
     * Logs a completion message if the 'finalize' log type is enabled.
     */
    finalize(() => {
      logIfEnabled('finalize', () => {
        logDebug(debugMode, `Request to ${req.url} completed.`);
      });
    })
  );
};

/**
 * Logs debugging information if logging is enabled.
 * @param enableLogging - Flag to toggle logging
 * @param message - Message to log
 */
function logDebug(enableLogging: boolean, message: string): void {
  if (enableLogging) {
    console.debug(`[DebugInterceptor] ${message}`);
  }
}
